import type { IAccountRequisiteDetails } from "@/modules/account/common/types";
import { useGetAccountRequisitesQuery } from "@/modules/account/services";
import { Select } from "@/shared/components";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import { Text } from "@ab.uitools/ui-library/components/Text";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export const AccountRequisites = () => {
  const { t } = useTranslation();
  const { accountId = "" } = useParams();
  const [details, setDetails] = useState<IAccountRequisiteDetails[]>([]);
  const [selectedRequisite, setSelectedRequisite] = useState<number | null>(null);

  const { data: requisites, isFetching } = useGetAccountRequisitesQuery({ accountId });

  const onChangeRequisiteType = (requisiteId: number) => {
    if (!requisiteId) return;
    setSelectedRequisite(requisiteId);
    const selectedRequisite = requisites?.find(
      (requisite) => requisite.id === requisiteId
    );
    if (selectedRequisite) {
      setDetails(selectedRequisite?.details);
    }
  };

  const getReplacedRequisiteDetail = (detail: string) => {
    return detail.replace(/<br\s*\/?>/g, "");
  };

  const requisiteTypeOptions = useMemo(() => {
    if (!requisites?.length) return [];
    return requisites?.map((requisite) => ({
      value: requisite.id,
      label: requisite.name,
    }));
  }, [requisites]);

  useEffect(() => {
    if (requisites?.length) {
      const id = requisites[0].id;
      onChangeRequisiteType(id);
    }
  }, [requisites]);

  return (
    <div className="main-section">
      <div className="main-section-content pb-16">
        <Heading className="content-title" weight="bolder" size="xsmall">
          {t("accounts.requisites.title")}
        </Heading>
        <Divider color="light" className="my-24" type="primary" isHorizontal/>
        <div>
          <Select
            label={t("accounts.requisites.type")}
            isLoading={isFetching}
            selectedValue={selectedRequisite}
            setSelectedValue={(value) => onChangeRequisiteType(value as number)}
            options={requisiteTypeOptions}
          />
          <div
            className={classNames("requisite-container mt-16", {
              "flexbox align-items--center full-height full-width": isFetching
            })}>
            {isFetching && (
              <div className="full-width flexbox justify-content--center align-items--center">
                <Spinner isFullScreen={false} size="small"/>
              </div>
            )}
            {details?.map((detail, i) => (
              <div key={i}>
                {!!i && (
                  <Divider className="mb-8" color="light" type="primary" isHorizontal/>
                )}
                <div className="flexbox justify-content--between align-items--center pt-4 pb-12">
                  <Text className="text-nowrap">{detail?.title}:</Text>
                  <Text className="pl-16 text-right" weight="semibold">
                    {getReplacedRequisiteDetail(detail?.value)}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
